import { Icon, onMount, reactive } from 'cdui-js';
import { CheckBox } from '../../../../components/CheckBox';
import { LastNextBtns } from '../../components/LastNextBtns';
import { lastProcess, nextProcess, pState, setFormValidateFunc } from '../../store';
import {
  signAgreementDoc,
  type AgreementSceneDocItem,
} from '../../../../../../api/agreement-doc';
import { signPdf } from '../../../../../../api/open-account';
import { openAgreementDoc } from '../../../../../../lib/agreement-doc-view';
import { fetchAgreementSceneBundleWithPreload } from '../../../../../../lib/agreement-pdf-cache';
import i18n from '../../../../../../i18n';
import { showToast, showToastError } from '../../../../components/Toast';

const SCENE_OPEN_ACCOUNT_CONFIRM_AND_SIGN = 'SCENE_OPEN_ACCOUNT_CONFIRM_AND_SIGN';
/** 衍生工具产品认识及风险披露：仅「有意买卖衍生产品」时展示/签署 */
const DOC_DERIVATIVES = 'AGR003';
/** 内地投资者书面声明：仅中国大陆身份时展示/签署 */
const DOC_MAINLAND_INVESTOR = 'AGR005';
/**
 * 香港投资者识别码制度客户同意书。
 * 需求：仅大陆身份隐藏；香港 / 澳门 / 其他国家地区均展示并签署。
 */
const DOC_HKIDR = 'AGR0012';

const PDFComp = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" stroke="none" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_819_1178)">
      <path
        d="M3.08173 0H13.0817L17.6971 4.61538V19.2308C17.6966 19.383 17.6514 19.5317 17.5671 19.6585C17.4831 19.7851 17.3631 19.8837 17.2225 19.9415C17.1292 19.9806 17.029 20.0005 16.9279 20H3.08173C2.87772 20 2.68206 19.9189 2.5378 19.7747C2.39354 19.6304 2.3125 19.4348 2.3125 19.2308V0.76923C2.31296 0.565318 2.39397 0.369847 2.53788 0.225385C2.68235 0.081472 2.87782 0.000463551 3.08173 0Z"
        fill="#FFD1C3"
      />
      <path
        d="M13.0811 0L17.6964 4.61538H13.8503C13.6724 4.61513 13.5 4.55332 13.3625 4.44045C13.2249 4.32758 13.1307 4.17061 13.0957 3.99615C13.086 3.94674 13.0811 3.89651 13.0811 3.84615V0Z"
        fill="#FFAD8D"
      />
      <path
        d="M18.7967 15.7308H1.20974C1.13921 15.7311 1.06883 15.7242 0.999735 15.71C0.930271 15.6969 0.862438 15.6762 0.797428 15.6485C0.732429 15.6209 0.670113 15.5875 0.611274 15.5485C0.552432 15.5099 0.497777 15.4653 0.448197 15.4154C0.398081 15.3658 0.353227 15.3111 0.314351 15.2523C0.275592 15.1935 0.242373 15.1311 0.21512 15.0662C0.17341 14.9683 0.147448 14.8644 0.138197 14.7585C0.134637 14.7237 0.13284 14.6888 0.132812 14.6539V9.60616C0.133039 9.42609 0.178254 9.24893 0.264351 9.09078C0.350127 8.93235 0.475144 8.79859 0.627428 8.70232L2.3382 7.60693V9.02078H17.6613V7.6077L19.3728 8.70616C19.5241 8.80357 19.6489 8.93706 19.7359 9.09463C19.8225 9.25254 19.8675 9.4299 19.8666 9.61001V14.6577C19.8672 14.7628 19.8514 14.8674 19.8197 14.9677C19.7786 15.1021 19.7117 15.2273 19.6228 15.3362C19.5553 15.4167 19.4772 15.4876 19.3905 15.5469C19.274 15.6258 19.143 15.6807 19.0051 15.7085C18.9364 15.7218 18.8666 15.7298 18.7967 15.7308Z"
        fill="#FF4000"
      />
      <path
        d="M8.16016 14.7988V10.5182H8.87997V9.8125H11.1106V10.5182H11.8383V12.6895H11.1106V13.3952H8.95907V14.7988H8.16016ZM8.95907 12.612H11.0315V10.5957H8.95907V12.612Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_819_1178">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

type SignRow =
  | { kind: 'text'; text: string; check: boolean }
  | { kind: 'doc'; doc: AgreementSceneDocItem; check: boolean };

/**
 * 协议行。注意项目 base.css 有 `* { flex: none }`，
 * 标题需 flex:1 + min-width:0 才能省略；箭头热区仅加宽，不抬高行高。
 */
const PdfDocLabel = (props: { doc: AgreementSceneDocItem }) => (
  <div class="flex align-center gap-16px" style={{ width: '100%', 'min-width': 0 }}>
    <div class="flex-shrink-0">
      <PDFComp />
    </div>
    <div class="single-line-ellipsis" style={{ flex: 1, 'min-width': 0 }}>
      {props.doc.name}
    </div>
    {/* 仅右侧箭头打开协议；勾选由外层 CheckBox 负责，避免整行误跳转 */}
    <div
      data-nav-only
      class="flex-shrink-0 flex align-center justify-end"
      style={{ cursor: 'pointer', width: '44px' }}
      onclick={(e) => {
        e.stopPropagation();
        openAgreementDoc(props.doc);
      }}
    >
      <Icon name="common-arrow-right"></Icon>
    </div>
  </div>
);

/** 声明文案（非协议文件）+ 接口 docs */
const buildSignList = (docs: AgreementSceneDocItem[]): SignRow[] => {
  const S = i18n.OpenProgress.Risk.Sign;
  return [
    { kind: 'text', text: S.OwnerDeclaration, check: false },
    { kind: 'text', text: S.TraderDeclaration, check: false },
    { kind: 'text', text: S.NeverRejected, check: false },
    ...docs.map((doc) => ({ kind: 'doc' as const, doc, check: false })),
    { kind: 'text', text: S.FinalConsent, check: false },
  ];
};

export const ConfirmAndSign = () => {
  const state = reactive({
    list: [] as SignRow[],
  });
  // 协议行 DOM 引用：校验失败时滚动到首个未勾选项
  const rowRefs: (HTMLDivElement | null)[] = [];

  const validateFunc = async () => {
    const unchecked = state.list.filter((i) => !i.check);
    if (unchecked.length !== 0) {
      showToast(i18n.OpenProgress.Risk.Sign.ToastAgreeRequired);
      const firstIdx = state.list.findIndex((i) => !i.check);
      rowRefs[firstIdx]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }
    return true;
  };

  const getDocCodesToSign = () =>
    state.list.filter((i): i is Extract<SignRow, { kind: 'doc' }> => i.kind === 'doc').map((i) => i.doc.docCode);

  onMount(() => {
    setFormValidateFunc({
      callback: validateFunc,
    });

    fetchAgreementSceneBundleWithPreload({
      sceneCode: SCENE_OPEN_ACCOUNT_CONFIRM_AND_SIGN,
      bizType: 'OPEN_ACCOUNT',
      bizId: pState.id ? String(pState.id) : undefined,
    })
      .then((bundle) => {
        // 仅投资经验页选择「有意买卖」时展示衍生品协议
        const needDeriv = String(pState.investmentExpForm.intendTradeDerivative || '') === '1';
        // 大陆身份以 identityType 为准（BACK 后 idCardType 可能已是境外证件）
        const isMainland =
          (pState.uploadLicForm.identityType || pState.uploadLicForm.idCardType) === 'MAINLAND_CHINA';
        const raw = Array.isArray(bundle?.docs) ? bundle.docs : [];
        // 单次 filter：条件互斥项在一趟里剔除，再按 sortNo 排序
        const docs = raw
          .filter((d): d is AgreementSceneDocItem => {
            if (!d?.docCode) return false;
            if (!needDeriv && d.docCode === DOC_DERIVATIVES) return false;
            if (!isMainland && d.docCode === DOC_MAINLAND_INVESTOR) return false;
            // 需求：AGR0012 仅大陆隐藏，其余身份均展示
            if (isMainland && d.docCode === DOC_HKIDR) return false;
            return true;
          })
          .sort((a, b) => (a.sortNo ?? 0) - (b.sortNo ?? 0));
        state.list = buildSignList(docs);
      })
      .catch((e) => {
        showToastError(e, i18n.Common.AgreementUnavailable);
        state.list = buildSignList([]);
      });
  });

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-L">{i18n.OpenProgress.Risk.Sign.Title}</div>
      {state.list.map((item, idx) => {
        return (
          <div ref={(el) => { rowRefs[idx] = el; }}>
            <CheckBox
              class="padding-y-S"
              active={item.check}
              change={(e) => {
                item.check = e;
              }}
            >
              <div class="color-D open-progress-step-desc" style={{ width: '100%', 'min-width': 0 }}>
                {item.kind === 'doc' ? (
                  <PdfDocLabel doc={item.doc} />
                ) : (
                  <div class="open-progress-step-desc">{item.text}</div>
                )}
              </div>
            </CheckBox>
          </div>
        );
      })}

      <LastNextBtns
        last={() => {
          lastProcess();
        }}
        next={async () => {
          const valid = await validateFunc();
          if (!valid) return;
          try {
            const docCodes = getDocCodesToSign();
            if (docCodes.length > 0) {
              const checkedAt = Date.now();
              await signAgreementDoc({
                sceneCode: SCENE_OPEN_ACCOUNT_CONFIRM_AND_SIGN,
                bizType: 'OPEN_ACCOUNT',
                bizId: String(pState.id),
                appClient: 'H5',
                items: docCodes.map((docCode) => ({
                  docCode,
                  evidenceJson: JSON.stringify({ checked: true, checkedAt }),
                })),
              });
            }
            // await signPdf({ id: pState.id });
            nextProcess();
          } catch (e) {
            showToastError(e);
          }
        }}
      />
    </div>
  );
};
